import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormContent } from '@hooks';

import { ApiEndpoint, FormType } from '@enums';

interface WafSummary {
  id: number | string;
  name: string;
  status?: string;
  rules?: number;
  lastUpdated?: string;
  configVersion?: string;
}

/**
 * Hook to retrieve WAF summary information
 *
 * This hook integrates with your existing store system to fetch and cache
 * WAF configuration data, with proper typing and performance optimizations.
 */
export const useWafSummary = (): WafSummary | null => {
  const { pathname } = useLocation();

  // Get the WAF data from the store
  const wafData = useFormContent({
    formType: FormType.CdnWAF,
    endpoint: ApiEndpoint.CUSTOM,
  });

  // Extract WAF ID from URL
  const wafId = useMemo(() => {
    const wafSegmentIndex = pathname.split('/').findIndex((segment) => segment === 'waf');
    if (wafSegmentIndex === -1 || wafSegmentIndex >= pathname.split('/').length - 1) {
      return null;
    }

    const potentialWafId = pathname.split('/')[wafSegmentIndex + 1];
    return /^\d+$/.test(potentialWafId) ? potentialWafId : null;
  }, [pathname]);

  // If we have WAF data and it matches the ID in the URL, use it
  // Otherwise return null or a minimal object with just the ID
  return useMemo(() => {
    if (!wafId) return null;

    if (wafData && String(wafData.id) === wafId) {
      return wafData;
    }

    // Return minimal WAF data if we don't have the full data yet
    return {
      id: wafId,
      name: `WAF ${wafId}`,
    };
  }, [wafId, wafData]);
};
